/**
 * SystemNotificationBlockingModal — full-screen non-dismissible modal
 * shown to every user when a system notification arrives.
 *
 * Owner-spec (PR-F, 2026-07-02):
 *   * `interaction_type='info'`     → title + content + single "סגור" button
 *   * `interaction_type='request'`  → two buttons: "זה בסדר" and
 *                                     "אני עובד/ת בזמן הזה". The second
 *                                     reveals a time-picker + "שלח" button.
 *   * Cannot dismiss via ESC / click-outside / close-X. The user must
 *     answer before the app becomes interactive again.
 *   * When `blocking=false`, the modal is shown but the user can defer
 *     without responding — used for non-critical announcements.
 *   * `severity` drives the accent colour: info=blue, warning=amber,
 *     critical=red.
 *   * Countdown to `start_time` shown when the notification carries a
 *     maintenance window.
 *
 * This component mounts at the App root and consumes the `useSystem
 * NotificationQueue` hook — it does NOT talk to the backend directly.
 * See App.js for wire-up.
 */
import React, { useState, useEffect, useMemo } from 'react';
import config from '../../config/config';

const API_BASE = config.api.baseUrl;

const SEVERITY_ACCENT = {
  info:     { bar: '#0369a1', tint: '#e0f2fe', pill: 'מידע' },
  warning:  { bar: '#b45309', tint: '#fef3c7', pill: 'אזהרה' },
  critical: { bar: '#b91c1c', tint: '#fee2e2', pill: 'קריטי' },
};

const CATEGORY_LABEL = {
  maintenance:  'עדכון מערכת',
  emergency:    'הודעת חירום',
  security:     'הודעת אבטחה',
  read_receipt: 'אישור קריאה',
  announcement: 'הודעה',
  custom:       'הודעה',
};

// Countdown widget — only mounts when we have a `target` timestamp.
const Countdown = ({ target }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!target) return null;
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) return <span style={{ color: '#b91c1c', fontWeight: 700 }}>התחיל</span>;
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  const s = Math.floor((diff % 60_000) / 1000);
  return (
    <span style={{ color: '#111827', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
      {String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}
    </span>
  );
};

const SystemNotificationBlockingModal = ({
  notification,
  onResponded,          // (updatedResponse) → void
  sessionId,
}) => {
  const [askingEndTime, setAskingEndTime] = useState(false);
  const [endTime, setEndTime] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const {
    id, category, interaction_type, severity, blocking,
    title, content, start_time, end_time,
    created_by_name, created_at,
  } = notification || {};

  const accent = SEVERITY_ACCENT[severity] || SEVERITY_ACCENT.info;
  const isRequest = interaction_type === 'request';

  // Report "seen" once on mount so the admin dashboard's last-seen
  // column populates even before the user clicks a button.
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        await fetch(`${API_BASE}/api/system-notifications/${id}/respond`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
          },
          body: JSON.stringify({ response: 'seen' }),
        });
      } catch (_) { /* fire-and-forget */ }
    })();
  }, [id, sessionId]);

  // Block ESC + prevent context menu when blocking=true.
  useEffect(() => {
    if (!blocking) return;
    const onKey = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onCtx = (e) => e.preventDefault();
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('contextmenu', onCtx, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('contextmenu', onCtx, true);
    };
  }, [blocking]);

  const submit = async (payload) => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API_BASE}/api/system-notifications/${id}/respond`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      const j = await r.json();
      onResponded && onResponded(j.response);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const startTimeIso = start_time || (notification?.maintenance_date && notification?.maintenance_start_time
    ? `${notification.maintenance_date}T${notification.maintenance_start_time}`
    : null);

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', inset: 0,
        zIndex: 2147483647,               // higher than any app z-index
        background: 'rgba(15, 23, 42, 0.85)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        direction: 'rtl', fontFamily: 'inherit',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          width: 'min(560px, 92vw)',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          borderTop: `6px solid ${accent.bar}`,
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 24px 12px', background: accent.tint }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{
              padding: '2px 10px',
              borderRadius: 999,
              background: accent.bar,
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.3,
            }}>
              {accent.pill}
            </span>
            <span style={{ color: '#374151', fontSize: 12, fontWeight: 600 }}>
              · {CATEGORY_LABEL[category] || CATEGORY_LABEL.custom}
            </span>
            {created_by_name && (
              <span style={{ marginRight: 'auto', color: '#6b7280', fontSize: 11 }}>
                נשלח ע"י {created_by_name}
              </span>
            )}
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
            {title || 'הודעת מערכת'}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 24px', overflowY: 'auto', flex: 1 }}>
          <div style={{
            whiteSpace: 'pre-wrap',
            fontSize: 15,
            lineHeight: 1.6,
            color: '#111827',
          }}>
            {content}
          </div>

          {startTimeIso && (
            <div style={{
              marginTop: 16,
              padding: '10px 14px',
              background: '#f8fafc',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ color: '#334155', fontSize: 12, fontWeight: 600 }}>ספירה לאחור:</span>
              <Countdown target={startTimeIso} />
              {end_time && (
                <span style={{ marginRight: 'auto', color: '#6b7280', fontSize: 12 }}>
                  עד {String(end_time).slice(11, 16)}
                </span>
              )}
            </div>
          )}

          {isRequest && askingEndTime && (
            <div style={{
              marginTop: 16,
              padding: 14,
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: 8,
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#1e3a8a' }}>
                באיזו שעה תסיים/י לעבוד?
              </div>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #93c5fd',
                  borderRadius: 6,
                  fontSize: 14,
                  fontFamily: 'inherit',
                  width: 140,
                }}
              />
            </div>
          )}

          {err && (
            <div style={{
              marginTop: 12,
              padding: '8px 12px',
              background: '#fef2f2',
              color: '#b91c1c',
              border: '1px solid #fecaca',
              borderRadius: 6,
              fontSize: 12,
            }}>
              {err}
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{
          padding: '14px 24px 20px',
          borderTop: '1px solid #f1f5f9',
          background: '#fafbfc',
          display: 'flex', gap: 10, justifyContent: 'flex-start',
        }}>
          {isRequest ? (
            askingEndTime ? (
              <>
                <button
                  disabled={busy || !endTime}
                  onClick={() => submit({
                    response: 'requested_delay',
                    requested_end_time: endTime,
                  })}
                  style={{
                    padding: '10px 22px',
                    background: '#0369a1',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: (busy || !endTime) ? 'not-allowed' : 'pointer',
                    opacity: (busy || !endTime) ? 0.6 : 1,
                    fontFamily: 'inherit',
                  }}
                >
                  {busy ? '...' : 'שלח'}
                </button>
                <button
                  disabled={busy}
                  onClick={() => { setAskingEndTime(false); setEndTime(''); }}
                  style={{
                    padding: '10px 20px',
                    background: '#fff',
                    color: '#334155',
                    border: '1px solid #cbd5e1',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  חזרה
                </button>
              </>
            ) : (
              <>
                <button
                  disabled={busy}
                  onClick={() => submit({ response: 'acknowledged' })}
                  style={{
                    padding: '10px 24px',
                    background: '#166534',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  זה בסדר
                </button>
                <button
                  disabled={busy}
                  onClick={() => setAskingEndTime(true)}
                  style={{
                    padding: '10px 24px',
                    background: '#fff',
                    color: '#b45309',
                    border: '2px solid #b45309',
                    borderRadius: 8,
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  אני עובד/ת בזמן הזה
                </button>
              </>
            )
          ) : (
            <button
              disabled={busy}
              onClick={() => submit({ response: 'acknowledged' })}
              style={{
                padding: '10px 24px',
                background: accent.bar,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              סגור
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemNotificationBlockingModal;
